import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lime_session_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Public
export const fetchPage = (slug) => api.get(`/pages/${slug}`).then(r => r.data);
export const fetchPublicSettings = () => api.get(`/settings/public`).then(r => r.data);
export const fetchQuote = (product, transmission) =>
  api.get(`/pricing/quote`, { params: { product, transmission } }).then(r => r.data);
export const fetchAvailability = (transmission, start, end) =>
  api.get(`/availability`, { params: { transmission, start, end } }).then(r => r.data);
export const submitEnquiry = (payload) => api.post(`/enquiry`, payload).then(r => r.data);
export const createCheckout = (payload) => api.post(`/checkout`, payload).then(r => r.data);
export const fetchCheckoutStatus = (sessionId) =>
  api.get(`/checkout/status/${sessionId}`).then(r => r.data);
export const fetchPosts = (params = {}) => api.get(`/posts`, { params }).then(r => r.data);
export const fetchPost = (slug) => api.get(`/posts/${slug}`).then(r => r.data);

// Auth
export const exchangeSession = (sessionId) =>
  api.post(`/auth/session`, null, { headers: { "X-Session-ID": sessionId } }).then(r => {
    if (r.data?.session_token) setToken(r.data.session_token);
    return r.data;
  });
export const fetchMe = () => api.get(`/auth/me`).then(r => r.data);
export const logout = () => api.post(`/auth/logout`).then(r => { setToken(null); return r.data; });

// Admin
export const adminBookings = () => api.get(`/admin/bookings`).then(r => r.data);
export const adminReschedule = (payload) =>
  api.post(`/admin/reschedule`, payload).then(r => r.data);
export const adminCreateManualBooking = (payload) =>
  api.post(`/admin/bookings/manual`, payload).then(r => r.data);
export const adminCancelBooking = (booking_id, slot_indexes) =>
  api.post(`/admin/bookings/${booking_id}/cancel`, slot_indexes && slot_indexes.length ? { slot_indexes } : {}).then(r => r.data);
export const adminOrders = (q = "") =>
  api.get(`/admin/orders`, { params: q ? { q } : {} }).then(r => r.data);
export const adminOrder = (id) => api.get(`/admin/orders/${id}`).then(r => r.data);
export const adminRefund = (id, payload) =>
  api.post(`/admin/orders/${id}/refund`, payload).then(r => r.data);
export const adminGetSettings = () => api.get(`/admin/settings`).then(r => r.data);
export const adminUpdateSettings = (payload) =>
  api.put(`/admin/settings`, payload).then(r => r.data);
export const adminStripeStatus = () =>
  api.get(`/admin/stripe/status`).then(r => r.data);
export const adminListPosts = () => api.get(`/admin/posts`).then(r => r.data);
export const adminCreatePost = (payload) => api.post(`/admin/posts`, payload).then(r => r.data);
export const adminUpdatePost = (id, payload) => api.put(`/admin/posts/${id}`, payload).then(r => r.data);
export const adminDeletePost = (id) => api.delete(`/admin/posts/${id}`).then(r => r.data);
export const adminUpdatePage = (slug, payload) =>
  api.put(`/admin/pages/${slug}`, payload).then(r => r.data);
export const adminEnquiries = () => api.get(`/admin/enquiries`).then(r => r.data);
export const adminReplyEnquiry = (enquiry_id, payload) =>
  api.post(`/admin/enquiries/${enquiry_id}/reply`, payload).then(r => r.data);
export const adminListHolidays = () => api.get(`/admin/holidays`).then(r => r.data);
export const adminCreateHoliday = (payload) =>
  api.post(`/admin/holidays`, payload).then(r => r.data);
export const adminDeleteHoliday = (date) =>
  api.delete(`/admin/holidays/${date}`).then(r => r.data);
export const adminDeleteHolidaySlot = (date, time) =>
  api.delete(`/admin/holidays/${date}/${encodeURIComponent(time)}`).then(r => r.data);
export const adminSyncMissingGCal = (only_imports = true) =>
  api.post(`/admin/google-calendar/sync-missing`, { only_imports }, { timeout: 600000 }).then(r => r.data);
export const adminSyncHolidaysGCal = () =>
  api.post(`/admin/google-calendar/sync-holidays`, null, { timeout: 600000 }).then(r => r.data);
export const adminResetAndResyncGCal = () =>
  // Returns immediately with 202; poll job-status for progress.
  api.post(`/admin/google-calendar/reset-and-resync`).then(r => r.data);
export const adminGCalJobStatus = () =>
  api.get(`/admin/google-calendar/job-status`).then(r => r.data);
export const adminSendTestEmail = (to) =>
  api.post(`/admin/email/test`, { to }).then(r => r.data);
export const adminTestCheckout = (payload) =>
  api.post(`/admin/test-checkout`, payload).then(r => r.data);

// Intensive course offers
export const adminListIntensiveOffers = () => api.get(`/admin/intensive-offers`).then(r => r.data);
export const adminCreateIntensiveOffer = (payload) =>
  api.post(`/admin/intensive-offers`, payload).then(r => r.data);
export const adminResendIntensiveOffer = (offer_id) =>
  api.post(`/admin/intensive-offers/${offer_id}/resend`).then(r => r.data);
export const adminCancelIntensiveOffer = (offer_id) =>
  api.post(`/admin/intensive-offers/${offer_id}/cancel`).then(r => r.data);
export const publicGetIntensiveOffer = (offer_id) =>
  api.get(`/intensive-offer/${offer_id}`).then(r => r.data);
export const publicOfferCheckout = (offer_id) =>
  api.post(`/intensive-offer/${offer_id}/checkout`).then(r => r.data);
